'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, Loader2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSubmitPageVerdictMutation } from '../services/ocr-qa.mutations';
import type { OcrQaResult, OcrQaTier, OcrQaVerdict } from '../types';

interface Props {
  tier: OcrQaTier;
  containerId: number;
  result: OcrQaResult;
  /** GLOBAL 1-based page this verdict is about. */
  pageNumber: number;
  /** Display label for that page ("PG-002"), when the page list knows it. */
  pageLabel: string | null;
  verdict: OcrQaVerdict | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Verdict on ONE page.
 *
 * There is nothing to choose here — the page came from the section the reviewer
 * clicked — so a reject asks for exactly one thing: the reason. An earlier
 * version made the reviewer tick a checkbox to unlock the field, and before
 * that it demanded a reason for every page in the container.
 */
export function OcrQaPageVerdictDialog({
  tier,
  containerId,
  result,
  pageNumber,
  pageLabel,
  verdict,
  onClose,
  onSuccess
}: Props) {
  const t = useTranslations('ocrQa');
  const [notes, setNotes] = useState('');
  const mutation = useSubmitPageVerdictMutation();

  const isOpen = verdict !== null;
  const isReject = verdict === 'rejected';
  const canSubmit = !isReject || notes.trim() !== '';
  const label = pageLabel
    ? t('pageSectionTitle', { number: pageLabel })
    : `#${pageNumber}`;

  function handleSubmit() {
    if (!verdict || !canSubmit) return;
    mutation.mutate(
      {
        tier,
        resultId: result.id,
        pageNumber,
        containerId,
        verdict,
        notes: notes.trim() || undefined
      },
      {
        onSuccess: () => {
          setNotes('');
          onClose();
          onSuccess?.();
        }
      }
    );
  }

  function handleOpenChange(next: boolean) {
    if (!next && !mutation.isPending) {
      setNotes('');
      onClose();
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            {isReject ? (
              <X className='text-red-600' />
            ) : (
              <Check className='text-green-600' />
            )}
            {isReject
              ? t('confirmRejectPageTitle', { page: label })
              : t('confirmAcceptPageTitle', { page: label })}
          </DialogTitle>
          <DialogDescription>
            {isReject
              ? t('confirmRejectPageDescription')
              : t('confirmAcceptPageDescription')}
          </DialogDescription>
        </DialogHeader>

        {isReject && (
          <div className='space-y-2'>
            <Label htmlFor='ocr-qa-page-note' className='text-sm'>
              {t('rejectionReasonLabel')}
            </Label>
            <Textarea
              id='ocr-qa-page-note'
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('pageNotePlaceholder')}
              rows={3}
              autoFocus
              disabled={mutation.isPending}
              className={!canSubmit ? 'border-red-500/50' : undefined}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => handleOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button
            variant={isReject ? 'destructive' : 'default'}
            onClick={handleSubmit}
            disabled={mutation.isPending || !canSubmit}
          >
            {mutation.isPending && (
              <Loader2 className='me-2 h-4 w-4 animate-spin' />
            )}
            {isReject ? t('rejectPage') : t('acceptPage')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
